/**
 * Hook for the laatumatriisi grid in Tilannekatsaus.
 *
 * One query per hanke: työvaiheet as rows, the five Congrid titles as columns.
 * Skipped until a hanke is selected. `refetch` is exposed so TaskDialog's
 * onSubtasksChanged can repaint a cell after topic edits, the way the overview
 * table already refreshes itself.
 */

import { useQuery, gql } from "@apollo/client";

export interface QualityMatrixItem {
  taskId: string;
  locationName: string | null;
  parentLocationName: string | null;
  /** Set only for Tila and Alue locations. */
  grandparentLocationName: string | null;
  dueDate: string | null;
  doneTopics: number;
  totalTopics: number;
  externalInspectionStatus: string | null;
}

export interface QualityMatrixCell {
  title: string;
  items: QualityMatrixItem[];
}

export interface QualityMatrixRow {
  sectionId: string;
  code: string | null;
  name: string | null;
  /** Päälittera the row belongs to (1-7), null when the code resolves to none. */
  headsectionNumber: number | null;
  headsectionLabel: string | null;
  cells: QualityMatrixCell[];
}

export const GET_QUALITY_MATRIX = gql`
  query GetQualityMatrix($projectId: String!) {
    qualityMatrix(projectId: $projectId) {
      rows {
        sectionId
        code
        name
        headsectionNumber
        headsectionLabel
        cells {
          title
          items {
            taskId
            locationName
            parentLocationName
            grandparentLocationName
            dueDate
            doneTopics
            totalTopics
            externalInspectionStatus
          }
        }
      }
    }
  }
`;

interface QualityMatrixQueryResult {
  qualityMatrix: {
    rows: QualityMatrixRow[];
  };
}

export function useQualityMatrix(projectId: string | null) {
  const { data, loading, error, refetch } = useQuery<QualityMatrixQueryResult>(
    GET_QUALITY_MATRIX,
    {
      variables: { projectId },
      skip: !projectId,
      fetchPolicy: "cache-and-network",
    },
  );

  return {
    rows: data?.qualityMatrix.rows ?? [],
    loading,
    error,
    refetch,
  };
}
